/**
 * UI string localization for the Daily Pañcāṅga surfaces.
 *
 * The API already localizes panchanga *values* (tithi/nakṣatra names, māsa,
 * connectives like यावत्/వరకు/ವರೆಗೆ). This dictionary localizes the surrounding
 * UI *chrome* — section labels, card titles, helper words — which was
 * previously hardcoded in English/IAST across every locale.
 *
 * Convention:
 *  - `en` keeps the scholarly IAST forms already in use (Saṃvatsara, Tithi…).
 *  - `sa`/`hi` use Devanāgarī, `te` Telugu, `kn` Kannada.
 *  - `then` is the leading sequence connective. It is intentionally EMPTY for
 *    non-English locales: the backend value already ends with a natural
 *    postpositional connective (अनन्तरम् / తర్వాత / ನಂತರ), so prefixing a word
 *    would be redundant. English strips the trailing "afterwards" and leads
 *    with "then " instead.
 */

import { Locale } from '../types/api';

export type UiStringKey =
  // Context band labels
  | 'samvatsara' | 'ayana' | 'ritu' | 'masa' | 'paksha' | 'amanta' | 'purnimanta' | 'adhika'
  // Five-aṅga limb labels
  | 'tithi' | 'vara' | 'nakshatra' | 'yoga' | 'karana'
  // Sun/moon + helper words
  | 'sunrise' | 'sunset' | 'moonrise' | 'allDay' | 'lit' | 'then' | 'till' | 'afterwards' | 'thenWord' | 'andWord'
  // Titles + links
  | 'dinaPanchanga' | 'howToRead' | 'kalaVisesha' | 'avoid' | 'favourable' | 'howCalculated'
  // Action buttons
  | 'alignmentChart' | 'share' | 'copy'
  // Homa recommendations card
  | 'homaTitle' | 'homaWhatLink' | 'homaGoodDay' | 'homaBeginners' | 'deityInfo' | 'homaFooter';

const STRINGS: Record<UiStringKey, Record<Locale, string>> = {
  // ── Context band ──────────────────────────────────────────────────
  samvatsara: { en: 'Saṃvatsara', sa: 'संवत्सर', hi: 'संवत्सर', te: 'సంవత్సర', kn: 'ಸಂವತ್ಸರ' },
  ayana:      { en: 'Ayana',      sa: 'अयन',     hi: 'अयन',     te: 'అయన',      kn: 'ಅಯನ' },
  ritu:       { en: 'Ṛtu',        sa: 'ऋतु',     hi: 'ऋतु',     te: 'ఋతువు',    kn: 'ಋತು' },
  masa:       { en: 'Māsa',       sa: 'मास',     hi: 'मास',     te: 'మాసం',     kn: 'ಮಾಸ' },
  paksha:     { en: 'Pakṣa',      sa: 'पक्ष',    hi: 'पक्ष',    te: 'పక్షం',    kn: 'ಪಕ್ಷ' },
  amanta:     { en: 'amānta',     sa: 'अमान्त',  hi: 'अमान्त',  te: 'అమాంత',    kn: 'ಅಮಾಂತ' },
  purnimanta: { en: 'pūrṇimānta', sa: 'पूर्णिमान्त', hi: 'पूर्णिमान्त', te: 'పూర్ణిమాంత', kn: 'ಪೂರ್ಣಿಮಾಂತ' },
  // Same words as data/translation_maps "Adhika", so masa_info renders exactly what the sentence did (B-139-MASA).
  adhika:     { en: 'Adhika',     sa: 'अधिक',   hi: 'अधिक',   te: 'అధిక',     kn: 'ಅಧಿಕ' },

  // ── Five aṅgas ────────────────────────────────────────────────────
  tithi:     { en: 'Tithi',    sa: 'तिथि',    hi: 'तिथि',    te: 'తిథి',      kn: 'ತಿಥಿ' },
  vara:      { en: 'Vāra',     sa: 'वार',     hi: 'वार',     te: 'వారం',     kn: 'ವಾರ' },
  nakshatra: { en: 'Nakṣatra', sa: 'नक्षत्र', hi: 'नक्षत्र', te: 'నక్షత్రం', kn: 'ನಕ್ಷತ್ರ' },
  yoga:      { en: 'Yoga',     sa: 'योग',     hi: 'योग',     te: 'యోగం',     kn: 'ಯೋಗ' },
  karana:    { en: 'Karaṇa',   sa: 'करण',     hi: 'करण',     te: 'కరణం',     kn: 'ಕರಣ' },

  // ── Sun/moon + helper words ───────────────────────────────────────
  sunrise:  { en: 'Sunrise',  sa: 'सूर्योदय',  hi: 'सूर्योदय',  te: 'సూర్యోదయం',    kn: 'ಸೂರ್ಯೋದಯ' },
  sunset:   { en: 'Sunset',   sa: 'सूर्यास्त', hi: 'सूर्यास्त', te: 'సూర్యాస్తమయం',  kn: 'ಸೂರ್ಯಾಸ್ತ' },
  moonrise: { en: 'Moonrise', sa: 'चन्द्रोदय', hi: 'चन्द्रोदय', te: 'చంద్రోదయం',    kn: 'ಚಂದ್ರೋದಯ' },
  allDay:   { en: 'all day',  sa: 'दिनपर्यन्तम्', hi: 'दिनभर', te: 'రోజంతా',    kn: 'ದಿನವಿಡೀ' },
  lit:      { en: 'lit',      sa: 'प्रकाशित',  hi: 'प्रकाशित',  te: 'ప్రకాశితం',   kn: 'ಪ್ರಕಾಶಿತ' },
  then:     { en: 'then ',    sa: '',          hi: '',          te: '',            kn: '' },
  // Timing connectives for rows built from structured `angas` (B-139-WEB). Same words
  // as data/translation_maps, so the card reads like the server sentence: en
  // "till 11:10" before the time, the others "11:10 వరకు" after it. `afterwards`
  // trails the next aṅga's name outside English (en leads with "then " instead).
  till:       { en: 'till',  sa: 'यावत्',    hi: 'तक',      te: 'వరకు',     kn: 'ವರೆಗೆ' },
  afterwards: { en: '',      sa: 'अनन्तरम्', hi: 'बाद में', te: 'తర్వాత',   kn: 'ನಂತರ' },
  // Karaṇa's sequence connectives outside English ("A, ఆపై B మరియు C"), from the
  // translation maps' ", then " / " and ": without them the Telugu line is not a
  // sentence (author, 2026-10-12). English keeps the card's own "then …, then …".
  thenWord:   { en: 'then',  sa: 'ततः',      hi: 'फिर',     te: 'ఆపై',      kn: 'ನಂತರ' },
  andWord:    { en: 'and',   sa: 'च',        hi: 'और',      te: 'మరియు',    kn: 'ಮತ್ತು' },

  // ── Titles + links ────────────────────────────────────────────────
  dinaPanchanga: { en: 'Dina Pañcāṅga', sa: 'दिन पञ्चाङ्ग', hi: 'दिन पञ्चाङ्ग', te: 'దిన పంచాంగం', kn: 'ದಿನ ಪಂಚಾಂಗ' },
  howToRead: {
    en: 'How to read the Pañcāṅga →',
    sa: 'पञ्चाङ्गं कथं पठनीयम् →',
    hi: 'पञ्चाङ्ग कैसे पढ़ें →',
    te: 'పంచాంగం ఎలా చదవాలి →',
    kn: 'ಪಂಚಾಂಗವನ್ನು ಹೇಗೆ ಓದುವುದು →',
  },
  kalaVisesha: { en: 'Kāla Viśeṣa', sa: 'काल विशेष', hi: 'काल विशेष', te: 'కాల విశేష', kn: 'ಕಾಲ ವಿಶೇಷ' },
  // "Avoid" = the inauspicious windows (Rāhu Kāla, Yama Gaṇḍa…); "Favourable"
  // = auspicious (Abhijit, Amṛta Kāla). Use the śubha/aśubha pairing — the
  // conventional Jyotiṣa framing — and NOT varjya-* words, which collide with
  // the specifically-named Varjyam window.
  avoid:       { en: 'Avoid',       sa: 'अशुभ', hi: 'अशुभ', te: 'అశుభం', kn: 'ಅಶುಭ' },
  favourable:  { en: 'Favourable',  sa: 'शुभ',  hi: 'शुभ',  te: 'శుభం',  kn: 'ಶುಭ' },
  howCalculated: {
    en: 'How is this calculated? →',
    sa: 'एतत् कथं गण्यते? →',
    hi: 'यह कैसे गणना होती है? →',
    te: 'ఇది ఎలా లెక్కించబడుతుంది? →',
    kn: 'ಇದನ್ನು ಹೇಗೆ ಲೆಕ್ಕಿಸಲಾಗುತ್ತದೆ? →',
  },

  // ── Action buttons ────────────────────────────────────────────────
  alignmentChart: { en: 'Alignment chart', sa: 'ग्रह चक्र', hi: 'ग्रह चक्र', te: 'గ్రహ చక్రం', kn: 'ಗ್ರಹ ಚಕ್ರ' },
  share:          { en: 'Share',           sa: 'प्रेषण',   hi: 'साझा करें', te: 'పంచుకోండి', kn: 'ಹಂಚಿಕೊಳ್ಳಿ' },
  copy:           { en: 'Copy',            sa: 'प्रतिलिपि', hi: 'प्रतिलिपि', te: 'కాపీ',      kn: 'ನಕಲು' },

  // ── Homa recommendations ──────────────────────────────────────────
  homaTitle:  { en: 'Homa Recommendations', sa: 'होम अनुशंसा', hi: 'होम अनुशंसा', te: 'హోమ సిఫారసులు', kn: 'ಹೋಮ ಶಿಫಾರಸುಗಳು' },
  homaWhatLink: {
    en: 'What is Homa and how recommendations work →',
    sa: 'होमः किम्, अनुशंसाः कथम् →',
    hi: 'होम क्या है और अनुशंसा कैसे →',
    te: 'హోమం అంటే ఏమిటి, సిఫారసులు ఎలా →',
    kn: 'ಹೋಮ ಎಂದರೇನು, ಶಿಫಾರಸುಗಳು ಹೇಗೆ →',
  },
  homaGoodDay: {
    en: 'A good day for Homa (Havan) to these deities:',
    sa: 'अद्य होम (हवन) हेतु शुभाः देवताः:',
    hi: 'आज होम (हवन) के लिए शुभ देवता:',
    te: 'నేడు హోమం (హవనం)కు శుభ దేవతలు:',
    kn: 'ಇಂದು ಹೋಮ (ಹವನ)ಕ್ಕೆ ಶುಭ ದೇವತೆಗಳು:',
  },
  homaBeginners: {
    en: 'Beginners can start with Homa of their Iṣṭa Devatā or Lord Gaṇeśa.',
    sa: 'आरम्भकाः स्व-इष्टदेवतायाः गणेशस्य वा होमेन आरभेरन्.',
    hi: 'आरंभकर्ता अपने इष्ट देवता या गणेश जी के होम से आरंभ कर सकते हैं.',
    te: 'ప్రారంభకులు తమ ఇష్ట దేవత లేదా గణేశుని హోమంతో ప్రారంభించవచ్చు.',
    kn: 'ಪ್ರಾರಂಭಿಕರು ತಮ್ಮ ಇಷ್ಟ ದೇವತೆ ಅಥವಾ ಗಣೇಶನ ಹೋಮದಿಂದ ಪ್ರಾರಂಭಿಸಬಹುದು.',
  },
  deityInfo: { en: 'Deity information', sa: 'देवता विवरण', hi: 'देवता जानकारी', te: 'దేవత సమాచారం', kn: 'ದೇವತೆ ಮಾಹಿತಿ' },
  homaFooter: {
    en: 'For guidance on Simplest, Simple and Elaborate process to perform Homa, refer to',
    sa: 'होमस्य सरलतम-सरल-विस्तृत-विधीनां मार्गदर्शनार्थं पश्यतु',
    hi: 'होम की सरलतम, सरल और विस्तृत विधि के मार्गदर्शन हेतु देखें',
    te: 'హోమం యొక్క సరళతమ, సరళ, విస్తృత విధానాల మార్గదర్శనానికి చూడండి',
    kn: 'ಹೋಮದ ಸರಳತಮ, ಸರಳ ಮತ್ತು ವಿಸ್ತೃತ ವಿಧಾನಗಳ ಮಾರ್ಗದರ್ಶನಕ್ಕೆ ನೋಡಿ',
  },
};

/** Look up a UI string for the given locale, falling back to English. */
export const uiString = (locale: Locale | string | undefined, key: UiStringKey): string => {
  const row = STRINGS[key];
  const loc = (locale as Locale) in row ? (locale as Locale) : 'en';
  return row[loc];
};
